import type { ActionContext } from 'vuex';
import { GeneralHelper } from '../../helper/general';
import type { HttpHelper } from '../../helper/http';

const helper = new GeneralHelper();

export interface INotification {
	notificationKey: string,
	dismissKey: string,
	title: string,
	message: string,
	type: string,
	progress?: number,
	dismissible?: boolean,
	applicationMuid?: string,
	environmentId?: number,
	snapshotMuid?: string,
	url?: string,
	repeated?: number,
}

interface INotificationState {
	notifications: INotification[],
	activeNotifications: INotification[],
	pastNotifications: INotification[],
	default_timeout: number,
	is_suppressing: boolean,
	base_notification: Partial<INotification> & {
		id: false | number,
		timeout: false | ReturnType<typeof setTimeout>,
		repeated: number,
		presence: string,
	},
}

export default {
	namespaced: true,
	state: (): INotificationState => ({
		notifications: [],
		activeNotifications: [],
		pastNotifications: [],
		default_timeout: 5,
		is_suppressing: false,
		base_notification: {
			id: false,
			type: 'notice',
			message: '',
			timeout: false,
			repeated: 0,
			presence: 'passive',
		},
	}),
	mutations: {
		ADD_NOTIFICATION: (state: INotificationState, notification: Partial<INotification>, isActive = false) => {
			if (state.is_suppressing) return;

			const baseNotification = helper.deepClone(state.base_notification);
			const newNotification = Object.assign(baseNotification, notification);
			newNotification.id = new Date().valueOf();

			if (!isActive) {
				newNotification.timeout = setTimeout((state: INotificationState, id: number) => {
					const index = helper.findObjectByProperty(state.notifications, 'id', id);
					const currentNotification = state.notifications.splice(index, 1)[0];

					const nMessages: Array<string> = state.pastNotifications.map(n => n.message ?? '');
					const nMessageIndex = nMessages.indexOf(currentNotification.message ?? '');

					if (nMessageIndex === -1) {
						state.pastNotifications.unshift(currentNotification);
					} else {
						const repetition = (currentNotification.repeated ?? 0) + 1;
						state.pastNotifications[nMessageIndex].repeated = (state.pastNotifications[nMessageIndex].repeated ?? 0) + repetition;
					}
				}, (state.default_timeout * 1000), state, newNotification.id);
			}

			state.notifications.push(newNotification);
		},

		REMOVE_NOTIFICATION: (state: INotificationState, notification_id: number) => {
			const notificationIndex = helper.findObjectByProperty(state.notifications, 'id', notification_id);
			clearTimeout((state.notifications[notificationIndex] as any).timeout);
			state.notifications.splice(notificationIndex, 1);
		},

		CLEAR_NOTIFICATIONS: (state: INotificationState) => {
			state.notifications = [];
			state.activeNotifications = [];
			state.pastNotifications = [];
		},

		SET_ACTIVE_NOTIFICATIONS: (state: INotificationState, notifications: INotification[]) => {
			// Backend already filters dismissed notifications — no client-side filtering needed.
			state.activeNotifications = notifications;
		},

		DISMISS_NOTIFICATION: (state: INotificationState, dismissKey: string) => {
			state.activeNotifications = state.activeNotifications.filter(
				n => n.dismissKey !== dismissKey,
			);
		},

		SET_SUPPRESSION: (state: INotificationState, status: boolean) => {
			state.is_suppressing = status;
		},
	},
	actions: {
		add: (context: ActionContext<INotificationState, any>, notification: Partial<INotification>) => {
			if (context.state.is_suppressing) return;

			const nMessages: Array<string> = context.state.notifications.map(n => n.message ?? '');
			const nMessageIndex = nMessages.indexOf(notification.message ?? '');

			if (nMessageIndex > -1) {
				context.state.notifications[nMessageIndex].repeated = (context.state.notifications[nMessageIndex].repeated ?? 0) + 1;
				return;
			}

			context.commit('ADD_NOTIFICATION', notification);
		},

		remove: (context: ActionContext<INotificationState, any>, notification_id: number) => {
			context.commit('REMOVE_NOTIFICATION', notification_id);
		},

		/**
		 * Dismiss a single notification by its stable dismissKey.
		 * Optimistically removes from store, then persists to backend.
		 * On failure re-fetches so the notification reappears rather than staying silently gone.
		 */
		dismissOne: async (
			context: ActionContext<INotificationState, any>,
			{ dismissKey, http }: { dismissKey: string, http: HttpHelper },
		): Promise<void> => {
			context.commit('DISMISS_NOTIFICATION', dismissKey);

			try {
				await http.post(
					`/api/notifications/${encodeURIComponent(dismissKey)}/dismiss`,
					[],
					{ autoRefresh: 'auto' },
				);
			} catch {
				const response = await http.post('/api/notifications', [], { autoRefresh: 'auto' });

				if (response.status === 200) {
					const result = await response.json();
					context.commit('SET_ACTIVE_NOTIFICATIONS', Array.isArray(result) ? result : []);
				}
			}
		},

		/**
		 * Dismiss all currently active notifications.
		 * Optimistically clears store, then persists each dismiss to backend in parallel.
		 */
		dismissAll: async (
			context: ActionContext<INotificationState, any>,
			{ http }: { http: HttpHelper },
		): Promise<void> => {
			const dismissible = context.state.activeNotifications.filter(
				n => n.dismissible !== false && n.dismissKey,
			);

			context.commit('CLEAR_NOTIFICATIONS');

			await Promise.allSettled(
				dismissible.map(n =>
					http.post(
						`/api/notifications/${encodeURIComponent(n.dismissKey)}/dismiss`,
						[],
						{ autoRefresh: 'auto' },
					),
				),
			);
		},

		setActive: (context: ActionContext<INotificationState, any>, notifications: INotification[]): void => {
			context.commit('SET_ACTIVE_NOTIFICATIONS', notifications);
		},

		clear: (context: ActionContext<INotificationState, any>) => {
			context.commit('CLEAR_NOTIFICATIONS');
		},

		suppress: (context: ActionContext<INotificationState, any>, seconds: number): void => {
			context.commit('SET_SUPPRESSION', true);
			setTimeout(() => {
				context.commit('SET_SUPPRESSION', false);
			}, 1000 * seconds);
		},
	},
};
